<template>
  <div
    class="v2-collection-products-page"
    :class="{ 'v2-collection-products-page--kp3462': isKp3462Layout }"
  >
    <!-- Loading -->
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading products..."
      />
    </div>

    <!-- Content
         Explicit `v-if="isLoaded"` (not `v-else`) — the bulk-add overlay
         below sits between the two siblings, which would otherwise pair
         the overlay's v-if with this v-else and unmount the page whenever
         the loader runs. Two independent v-ifs keep them orthogonal:
         page mounts as soon as isLoaded flips, regardless of overlay
         state. -->
    <v2-collection-unavailable
      v-if="isLoaded && isCollectionUnavailable && !isBrand"
      back-label="Go to Dashboard"
      @back="$router.push({ name: 'v2-dashboard' })"
    />
    <div
      v-if="isLoaded && !(isCollectionUnavailable && !isBrand)"
      class="v2-collection-products-page__body"
    >
      <!-- KP-3462 ON: full-width toolbar — title + all controls span both ends,
           above the filter + grid (reclaims horizontal space in the iframe). -->
      <div
        v-if="isKp3462Layout"
        class="v2-collection-products-page__controls"
      >
        <div class="v2-collection-products-page__left-controls">
          <!-- Back + collection name, inline at the left of the toolbar. -->
          <div class="v2-collection-products-page__header-title">
            <button
              class="v2-collection-products-page__back-btn"
              @click="goBack"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line
                  x1="19"
                  y1="12"
                  x2="5"
                  y2="12"
                />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
            <h1
              class="v2-heading-sm v2-m-0 v2-collection-products-page__title-text"
              :title="collectionData.collectionName || 'Collection Products'"
            >
              {{ collectionData.collectionName || 'Collection Products' }}
            </h1>
          </div>
          <div class="v2-collection-products-page__search">
            <svg
              class="v2-collection-products-page__search-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              />
              <line
                x1="21"
                y1="21"
                x2="16.65"
                y2="16.65"
              />
            </svg>
            <v2-input
              :value="filters.search"
              :placeholder="searchBarPlaceholderText"
              @input="filters.search = $event; searchProducts()"
            />
          </div>

          <!-- Select All -->
          <v2-button
            v-if="products.length > 0"
            variant="outline"
            @click="onToggleSelectAll"
          >
            <svg
              v-if="!isSelectedAllOnScreen"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {{ isSelectedAllOnScreen ? 'Deselect all' : 'Select all' }}
          </v2-button>

          <!-- Filter By Delivery Date -->
          <v2-filter-by-delivery-date
            v-if="!isGuest"
            v-model="selectedAvailabilityDate"
            :availability-dates="filterByAvailabilityDates"
            @apply="filterByDate"
          />

          <!-- Currency dropdown -->
          <div
            v-if="collectionData && collectionData.availableCurrencies && collectionData.availableCurrencies.length > 0"
            class="v2-collection-products-page__currency"
          >
            <v2-currency-dropdown
              :base-currency="collectionBaseCurrency"
              :selected-currency="filters.currency"
              :currencies="collectionData.availableCurrencies || []"
              @on-selected-currency="filterByCurrency"
            />
          </div>
        </div>

        <div class="v2-flex v2-items-center v2-gap-2 v2-collection-products-page__right-controls">
          <!-- View Toggle - Grid Icon -->
          <button
            class="v2-collection-products-page__view-btn"
            :class="{ 'v2-collection-products-page__view-btn--active': itemViewClass === PRODUCTS_VIEW.GRID_VIEW }"
            @click="onGridViewClick"
          >
            <large-grid-view-icon />
          </button>

          <!-- View Toggle - Compact Icon -->
          <button
            class="v2-collection-products-page__view-btn"
            :class="{ 'v2-collection-products-page__view-btn--active': itemViewClass === PRODUCTS_VIEW.COMPACT_GRID_VIEW }"
            @click="onCompactViewClick"
          >
            <compact-grid-view-icon />
          </button>

          <!-- Sort -->
          <v2-dropdown
            :options="sortByOptions"
            :value="selectedSortBy"
            label-key="text"
            value-key="value"
            align="right"
            @select="onSortChange"
          />

          <!-- View as Retailer -->
          <v2-direct-retailer-dropdown v-if="isBrand" />
        </div>
      </div>

      <!-- KP-3462 OFF: original header (Back + Title + View as Retailer). -->
      <div
        v-else
        class="v2-collection-products-page__header"
      >
        <button
          class="v2-collection-products-page__back-btn"
          @click="goBack"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="19"
              y1="12"
              x2="5"
              y2="12"
            />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <h1 class="v2-heading-sm v2-m-0">
          {{ collectionData.collectionName || 'Collection Products' }}
        </h1>
        <div class="v2-collection-products-page__header-actions">
          <v2-direct-retailer-dropdown v-if="isBrand" />
        </div>
      </div>

      <!-- Replace Product Banner -->
      <div
        v-if="replacingProduct"
        class="v2-collection-products-page__replace-banner"
      >
        <span class="v2-collection-products-page__replace-banner-text">
          Replacing: <strong>{{ replacingProduct.name }}</strong> — Select a product to replace it
        </span>
        <v2-button
          variant="outline"
          size="sm"
          @click="cancelReplace"
        >
          Cancel Replace
        </v2-button>
      </div>

      <!-- Retailer View Banner -->
      <div
        v-if="isBrand && selectedDirectRetailer"
        class="v2-collection-products-page__retailer-banner"
      >
        <div class="v2-collection-products-page__retailer-banner-content">
          <span class="v2-collection-products-page__retailer-banner-text">
            You are viewing as: <strong>{{ selectedDirectRetailer.entityName }} {{ selectedDirectRetailer.retailerBrandIdentity ? `(${selectedDirectRetailer.retailerBrandIdentity})` : '' }}</strong>
          </span>
          <v2-button
            variant="outline"
            size="sm"
            class="v2-collection-products-page__retailer-banner-button"
            @click="showDownloadModal = true"
          >
            Download Collection
          </v2-button>
        </div>
      </div>

      <div class="v2-collection-products-page__main">
        <!-- Sidebar filter -->
        <div
          v-if="!isCollectionUnavailable && hasFilterOptions"
          class="v2-collection-products-page__sidebar"
        >
          <shop-filter
            v-if="filterOptions.departments && filterOptions.departments.length"
            :filters="filters"
            :total-products="totalProducts"
            :filter-options="filterOptions"
            :whole-sale-enable="isShowWsp"
            :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
            @update-filters="fetchShopProducts"
            @close-sidebar="closeSideBar"
          />
        </div>

        <!-- Main content -->
        <div class="v2-collection-products-page__content">
          <!-- Controls bar — KP-3462 OFF only; when ON these controls live in
               the full-width toolbar at the top of the page. -->
          <div
            v-if="!isKp3462Layout"
            class="v2-collection-products-page__controls"
          >
            <div class="v2-collection-products-page__left-controls">
              <div class="v2-collection-products-page__search">
                <svg
                  class="v2-collection-products-page__search-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="8"
                  />
                  <line
                    x1="21"
                    y1="21"
                    x2="16.65"
                    y2="16.65"
                  />
                </svg>
                <v2-input
                  :value="filters.search"
                  :placeholder="searchBarPlaceholderText"
                  @input="filters.search = $event; searchProducts()"
                />
              </div>

              <!-- Select All -->
              <v2-button
                v-if="products.length > 0"
                variant="outline"
                @click="onToggleSelectAll"
              >
                <svg
                  v-if="!isSelectedAllOnScreen"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {{ isSelectedAllOnScreen ? 'Deselect all' : 'Select all' }}
              </v2-button>

              <!-- Filter By Delivery Date -->
              <v2-filter-by-delivery-date
                v-if="!isGuest"
                v-model="selectedAvailabilityDate"
                :availability-dates="filterByAvailabilityDates"
                @apply="filterByDate"
              />

              <!-- Currency dropdown -->
              <div
                v-if="collectionData && collectionData.availableCurrencies && collectionData.availableCurrencies.length > 0"
                class="v2-collection-products-page__currency"
              >
                <v2-currency-dropdown
                  :base-currency="collectionBaseCurrency"
                  :selected-currency="filters.currency"
                  :currencies="collectionData.availableCurrencies || []"
                  @on-selected-currency="filterByCurrency"
                />
              </div>
            </div>

            <div class="v2-flex v2-items-center v2-gap-2 v2-collection-products-page__right-controls">
              <!-- View Toggle - Grid Icon -->
              <button
                class="v2-collection-products-page__view-btn"
                :class="{ 'v2-collection-products-page__view-btn--active': itemViewClass === PRODUCTS_VIEW.GRID_VIEW }"
                @click="onGridViewClick"
              >
                <large-grid-view-icon />
              </button>

              <!-- View Toggle - Compact Icon -->
              <button
                class="v2-collection-products-page__view-btn"
                :class="{ 'v2-collection-products-page__view-btn--active': itemViewClass === PRODUCTS_VIEW.COMPACT_GRID_VIEW }"
                @click="onCompactViewClick"
              >
                <compact-grid-view-icon />
              </button>

              <!-- Sort -->
              <v2-dropdown
                :options="sortByOptions"
                :value="selectedSortBy"
                label-key="text"
                value-key="value"
                align="right"
                @select="onSortChange"
              />
            </div>
          </div>

          <!-- Group by (retailer) -->
          <div
            v-if="!isBrand"
            class="v2-flex v2-items-center v2-gap-2 v2-mb-4"
          >
            <span class="v2-text-sm v2-text-muted">Group Products by:</span>
            <v2-dropdown
              :options="groupOptions"
              :value="selectedGroup"
              @select="groupProducts($event)"
            />

            <!-- Wishlist selector -->
            <div
              v-if="selectedWishlist"
              class="v2-collection-products-page__wishlist-selector"
            >
              <v2-button
                variant="outline"
                @click="onClickSelectWishlist"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="v2-collection-products-page__heart-icon"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                {{ wishlistBtnLabel }}
              </v2-button>
            </div>
          </div>

          <!-- Scrollable products area -->
          <div
            ref="scrollArea"
            class="v2-collection-products-page__scroll-area"
          >
            <!-- Product Grid -->
            <div
              v-if="products.length"
              class="v2-collection-products-page__products"
            >
              <div
                v-for="(groupedProduct, gIndex) in products"
                :key="gIndex"
              >
                <!-- Group heading -->
                <div
                  v-if="groupedProduct._id && groupedProduct.products.length"
                  class="v2-collection-products-page__group-heading"
                >
                  {{ groupedProduct._id }}
                </div>

                <!-- Product cards -->
                <div
                  v-if="groupedProduct.products.length"
                  class="v2-collection-products-page__grid"
                  :class="itemViewClass === PRODUCTS_VIEW.COMPACT_GRID_VIEW ? 'v2-collection-products-page__grid--compact' : ''"
                >
                  <v2-product-card
                    v-for="(product, pIndex) in groupedProduct.products"
                    :key="product._id"
                    :product="{ productInfo: product, colors: product.colors, isInCart: product.isInCart }"
                    :show-checkbox="isShowWsp && product.price !== 'N/A'"
                    :is-checked="indexes.includes(product._id)"
                    :is-selection-mode="indexes.length > 0"
                    :show-actions="isRetailer && isShowWsp"
                    :actions="getProductActions(product)"
                    :show-wsp="isShowWsp"
                    :currency="filters.currency"
                    @check="onCheckboxChange(product, $event.checked)"
                    @click="onClickProduct(product)"
                    @action="onProductAction({ action: $event.action, product, index: pIndex, groupIndex: gIndex })"
                  >
                    <template
                      v-if="isShowWsp"
                      #footer
                    >
                      <v2-product-wishlists-dropdown
                        :product="product"
                        :wishlists="getWishlistsToShow(product)"
                        :product-wishlists-ids="getProductWishlists(product)"
                        :show-check-box="indexes.length > 0"
                        :disabled="inactiveCollection || isBrandNotAccessible || product.price === 'N/A'"
                        :is-selections="isBrand"
                        :product-wishlists-count="getProductWishlistsCount(product)"
                        :center-icons-when-no-label="true"
                        @select-wishlist-on-product="onSelectWishlistOnProduct(product, pIndex, gIndex, $event)"
                        @create-wishlist="onCreateWishlist(product, pIndex, gIndex)"
                        @toggle-wishlist-dropdown="onToggleWishlistDropdown(product, $event)"
                      />
                    </template>
                  </v2-product-card>
                </div>
              </div>

              <!-- Loading more (infinite scroll) -->
              <div
                v-if="continueLoading"
                ref="loadingIcon"
                class="v2-flex v2-justify-center v2-p-4"
              >
                <v2-spinner label="Loading more..." />
              </div>
            </div>

            <v2-collection-unavailable
              v-else-if="isCollectionUnavailable"
              :for-retailer-view="!!selectedDirectRetailer"
              @back="$router.push({ name: 'v2-collections' })"
            />
            <!-- Empty search -->
            <div
              v-else-if="filters.search"
              class="v2-text-center v2-p-8 v2-text-muted"
            >
              No products found
            </div>

            <!-- No products at all -->
            <v2-card
              v-else-if="totalProducts === 0"
              class="v2-collection-products-page__empty"
            >
              <h3 class="v2-heading-sm v2-mb-2">
                No Products Found
              </h3>
              <p class="v2-text-sm v2-text-muted v2-mb-4">
                This collection doesn't have any products yet.
              </p>
              <v2-button
                variant="primary"
                @click="goBack"
              >
                Back to Collections
              </v2-button>
            </v2-card>

            <!-- Empty wishlist filter message -->
            <div
              v-else-if="shouldShowEmptyWishlistMsg"
              class="v2-text-center v2-p-8 v2-text-muted"
            >
              No products found in this {{ selectedWishlist ? 'wishlist' : 'collection' }}.
            </div>
          </div>
        </div>
      </div>

      <!-- Download Catalog Modal -->
      <v2-download-catalog-modal
        :show="showDownloadModal"
        :collection="{ _id: collectionId }"
        :retailer-id="downloadRetailerId"
        @close="showDownloadModal = false"
      />

      <!-- Wishlist List Modal (for bulk add to selection/wishlist) -->
      <v2-wishlist-list-modal
        :show="showWishlistListModal"
        :wishlists="wishlists"
        :selected-wishlist="selectedWishlist"
        :selected-products="productList"
        @move-to-wishlist="moveAllToWishList"
        @unset-wishlist-selection="handleUnsetWishlist"
        @should-change-wishlist="handleShouldChangeWishlist"
        @close="showWishlistListModal = false"
      />

      <!-- Product Detail Modal (V2) -->
      <V2ProductDetailModal
        v-if="modalProductId"
        :visible="!!modalProductId"
        :product-id="modalProductId"
        :collection-id="collectionId"
        @close="modalProductId = null"
        @deleted="onProductDeleted"
      />

      <!-- Brand Add to Order Modal (V2) -->
      <!--
        `use-top-layer="false"`: this modal contains flatpickr date pickers
        (Requested Delivery Window in V2AddressSection, plus the cart-total
        picker). flatpickr appends its calendar to <body>, and a top-layer
        <dialog> (the V2Modal default) renders above every z-index — so the
        calendar always ended up BEHIND the modal with no way to lift it
        forward. Opting this modal out of top-layer rendering puts it back
        in the regular stacking context where flatpickr's default
        `z-index: 99999` correctly stacks above the modal's z-index (9000).
      -->
      <v2-modal
        v-if="isBrand"
        :show="showBrandCartModal"
        title="Add to Order"
        size="full"
        :body-id="brandCartModalId"
        :use-top-layer="false"
        @close="onCloseAddToCartModal"
      >
        <v2-brand-cart
          :cart-products="cartProducts"
          :collection-id="collectionId"
          :retailer-id="brandCartRetailerId"
          :is-loading-cart="isLoadingCart"
          :get-cart-products-by-brand="getCartProductsByBrand"
          :is-brand-cart-updated="isBrandCartUpdated"
          :modal-id="brandCartModalId"
          @close-brand-cart-modal="onCloseAddToCartModal"
          @get-full-selected-products="getFullSelectedProducts"
        />
      </v2-modal>

      <!-- Bottom Multi-Select Bar -->
      <transition name="v2-slide-up">
        <div
          v-if="selectCount > 0"
          class="v2-collection-products-page__multi-bar"
        >
          <div class="v2-collection-products-page__multi-bar-inner">
            <!-- Product count -->
            <div class="v2-collection-products-page__multi-count">
              <span class="v2-collection-products-page__multi-badge">{{ selectCount }}</span>
              {{ selectCount > 1 ? 'Products selected' : 'Product selected' }}
            </div>

            <!-- Select all -->
            <button
              class="v2-collection-products-page__multi-action"
              @click="forceToggleSelect(true)"
            >
              Select all
            </button>

            <!-- Deselect all -->
            <button
              class="v2-collection-products-page__multi-action"
              @click="forceToggleSelect(false)"
            >
              Deselect all
            </button>

            <!-- Brand: Add to order -->
            <button
              v-if="isBrand"
              class="v2-collection-products-page__multi-action"
              @click="onAddToOrder"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              Add to order
            </button>

            <!-- Download products -->
            <button
              class="v2-collection-products-page__multi-action"
              @click="onDownloadSelectedProducts"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line
                  x1="12"
                  y1="15"
                  x2="12"
                  y2="3"
                />
              </svg>
              Download products
            </button>

            <!-- Add to selection/wishlist -->
            <button
              class="v2-collection-products-page__multi-action"
              @click="onSelectOrCreateWishlist"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              {{ 'Add to wishlist' }}
            </button>

            <!-- Retailer: Remove from wishlist (if viewing a wishlist filter) -->
            <button
              v-if="isRetailer && isEnableRemoveWishlistButton"
              class="v2-collection-products-page__multi-action v2-collection-products-page__multi-action--danger"
              @click="onRemoveFromWishlist"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Remove from wishlist
            </button>

            <!-- Retailer: Add to cart -->
            <button
              v-if="isRetailer && isOrderEnableInShop"
              class="v2-collection-products-page__multi-action"
              @click="onMoveAllToCart"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle
                  cx="9"
                  cy="21"
                  r="1"
                />
                <circle
                  cx="20"
                  cy="21"
                  r="1"
                />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              Add to cart
            </button>
          </div>
        </div>
      </transition>
    </div>

    <!-- Full-screen overlay while "Add to order" (brand) / "Add to cart"
         (retailer) is resolving the selected products. Sits above the page
         and the bottom multi-select bar but below the modal it'll
         eventually open. Last sibling on purpose — keeps it independent
         of the page's v-if/v-else flow so it can't accidentally unmount
         the body when shown. -->
    <div
      v-if="isProcessingAddAction"
      class="v2-collection-products-page__add-action-overlay"
      role="status"
      aria-live="polite"
    >
      <v2-spinner
        size="lg"
        :label="isBrand ? 'Preparing your order...' : 'Adding products to cart...'"
      />
    </div>
  </div>
</template>

<script>
import analytics from '@/@core/utils/analytics'
import V2Modal from '@/components/v2/V2Modal.vue'
import constants, {
  DOWNLOAD_TYPES,
  FILE_FORMATS,
  FLAG_CONSTANTS,
  PRODUCTS_LIMIT,
  PRODUCTS_VIEW,
  searchBarPlaceholderText,
  wishlistAction,
} from '@/constants'
import store from '@/store'
import {
  DOWNLOAD_COLLECTION,
  FETCH_COLLECTION_PRODUCT_FILTERS,
 GET_PRODUCTS_IDS,
} from '@/store/modules/collection.module'
import {
  SET_IS_CREATE_WISHLIST_BTN_DISABLED,
  SET_IS_ENABLE_CREATE_WISHLIST,
  SET_IS_ENABLE_REMOVE_WISHLIST_BUTTON,
} from '@/store/modules/wishlist.module'
import { FETCH_DIRECT_RETAILERS } from '@/store/modules/connections.module'
import {
  GET_PRODUCTS_BY_IDS,
  FETCH_CART_PRODUCTS,
  UPDATE_SELECT_COUNT,
  UPDATE_SELECT_TYPE,
  MULTI_SELECT_PAGES,
  UPDATE_IS_SELECTED_ALL_PRODUCTS,
  FETCH_WISHLISTS,
  UPDATE_TOTAL_PRODUCTS_COUNT,
  ADD_PRODUCT_TO_WISHLIST,
  UPDATE_CART_ITEMS_COUNT,
  SET_REPLACE_CART_PRODUCT,
  UPDATE_SELECTED_DIRECT_RETAILER,
  CLEAR_DUPLICATE_ORDER_DATA,
} from '@/store/modules/shop.module'

import { SHOW_DOWNLOADS_NAV_INDICATOR, SHOW_WISHLIST_NAV_INDICATOR } from '@/store/modules/notification.module'
import { useResponsiveAppLeftSidebarVisibility } from '@core/comp-functions/ui/app'
import { apiToastSuccess, apiToastWarning } from '@core/utils/toast'
import {
  debounce,
  formatObject,
  textTruncate,
} from '@core/utils/utils'
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from '@vue/composition-api'
import Vue from 'vue'
import { mapState } from 'vuex'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import V2ProductCard from '@/components/v2/V2ProductCard.vue'
import V2DirectRetailerDropdown from '@/components/v2/V2DirectRetailerDropdown.vue'
import V2FilterByDeliveryDate from '@/components/v2/V2FilterByDeliveryDate.vue'
import LargeGridViewIcon from '@/@core/assets/svg-components/LargeGridViewIcon.vue'
import CompactGridViewIcon from '@/@core/assets/svg-components/CompactGridViewIcon.vue'
import V2CurrencyDropdown from '@/components/v2/V2CurrencyDropdown.vue'
import { useSelections } from '@/@core/comp-functions/selections/useSelection'
import V2ProductWishlistsDropdown from '@/components/v2/V2ProductWishlistsDropdown.vue'
import { useEcommerceUi } from '../../apps/useEcommerce'
import {
  useShopFiltersSortingAndPagination,
  useShopRemoteData,
  useShopUi,
} from '../../apps/shop/useECommerceShop'
import ShopFilter from '../../apps/shop/ShopFilter.vue'
import V2WishlistListModal from './V2WishlistListModal.vue'
import V2BrandCart from '../checkout/V2BrandCart.vue'
import UserRoleMixin from '../../apps/UserRoleMixin.vue'
import V2DownloadCatalogModal from './V2DownloadCatalogModal.vue'
import V2ProductDetailModal from '../product/V2ProductDetailModal.vue'
import {
  initializeFilterOptions,
  buildColorEntries,
  buildDerivedFilterState,
  stripUnchangedPriceFilters,
  processGroupedProducts,
  mergePagedProducts,
  trackCollectionOpenEvents,
  trackWishlistToggle,
  buildFetchParams,
  applyFilterMenuUpdates,
  applyDerivedFilterState,
  extractResultMetadata,
  processFullSelectedProductsResult,
  resolveFullSelectedProductsParams,
  extractCartProductsFromResponse,
  applyProductsFetchError,
} from './collectionProductsHelpers'
import { useCollectionAvailability, STALE_RESPONSE } from './useCollectionAvailability'

const { formatCurrency } = utils
const { BRAND_ACCESS, ROLES } = c
const { CONNECTION_REQUEST_STATUS, TRACKS } = constants
const { MULTI_SELECT_PRODUCTS } = TRACKS.ACTIONS

export default {
  name: 'V2CollectionProducts',
  components: {
    V2Modal,
    V2Spinner,
    V2Card,
    V2Button,
    V2CollectionUnavailable: () => import('./V2CollectionUnavailable.vue'),
    V2Input,
    V2Dropdown,
    V2ProductCard,
    V2DirectRetailerDropdown,
    V2FilterByDeliveryDate,
    LargeGridViewIcon,
    CompactGridViewIcon,
    V2CurrencyDropdown,
    ShopFilter,
    V2ProductWishlistsDropdown,
    V2WishlistListModal,
    V2BrandCart,
    V2DownloadCatalogModal,
    V2ProductDetailModal,
  },
  mixins: [UserRoleMixin],
  props: {
    collectionId: {
      type: String,
      default: null,
    },
  },
  setup(props, { root }) {
    const { sortBy, sortByOptions } = useShopFiltersSortingAndPagination()
    const {
      handleCartActionClick,
      toggleProductInWishlist,
      addMultipleItemsInCart,
      markItemsAsInCart,
    } = useEcommerceUi()
    const collectionData = ref({})
    const { isCollectionUnavailable, latest } = useCollectionAvailability()
    const selectedWishlistId = ref('')
    const selectedWishlist = ref(null)
    const wishlists = ref([])
    const isFetchingWishlists = ref(true)
    // Client-side map: productId → [wishlistId, ...] (API doesn't return this)
    const productWishlistsMap = ref({})
    const isDisableCreateWishlist = computed({
      get: () => root.$store.state.wishlist.isCreateWishlistBtnDisabled,
      set: val => {
        root.$store.commit(SET_IS_CREATE_WISHLIST_BTN_DISABLED, val)
      },
    })
    const shouldChangeWishlist = ref(false)

    const isBrand = computed(
      () => root.$store.getters.entityType === ROLES.BRAND,
    )
    const isRetailer = computed(
      () => root.$store.getters.entityType === ROLES.RETAILER,
    )
    const profile = computed(() => root.$store.getters.profile)

    const deselectedProducts = ref([])
    const fetchQueryParams = ref({})
    const collectionBaseCurrency = ref('')

    const onClickSelectWishlist = () => {
      shouldChangeWishlist.value = true
      isDisableCreateWishlist.value = true
      showWishlistListModal.value = true
    }

    const buildToggleProps = ({
 product, index, action, wishlistName, wishlist, isCreateWishlist, wishlistId,
}) => {
      const wishlistIdValue = !isCreateWishlist
        ? wishlistId || selectedWishlistId.value
        : null
      return {
        product,
        wishlistId: wishlistIdValue,
        action,
        wishlistName,
        wishlist,
        collectionName: collectionData.value?.collectionName,
        fullIdsList: index,
      }
    }

    const applyRemoveAllOverride = toggleProductProps => {
      toggleProductProps.wishlist = selectedWishlist.value
      toggleProductProps.action = wishlistAction.REMOVE_IN_WISHLIST
    }

    const handleBulkUpdate = (updatedProduct, index) => {
      updatedProduct.forEach((updatedProducts, ind) => {
        Vue.set(products.value, index[ind], updatedProducts)
      })
    }

    const handleSingleUpdate = (updatedProduct, product, index, groupIndex) => {
      if (products.value[groupIndex]?.products.length) {
        Vue.set(products.value[groupIndex].products, index, updatedProduct)
      }
      if (isRetailer.value) {
        trackWishlistToggle(product.isInWishlist, collectionData.value?.collectionName)
      }
    }

    const reactToggleProductInWishlist = async ({
      product,
      index,
      groupIndex,
      action,
      wishlistId = null,
      wishlistName = '',
      wishlist = null,
      isCreateWishlist = false,
    }) => {
      store.commit(UPDATE_SELECT_COUNT, 0)
      const toggleProductProps = buildToggleProps({
 product, index, action, wishlistName, wishlist, isCreateWishlist, wishlistId,
})
      const isRemoveAll = isSelectAll.value
        && selectedWishlist?.value?._id
        && action === wishlistAction.REMOVE_FROM_WISHLIST
      if (isRemoveAll) {
        applyRemoveAllOverride(toggleProductProps)
      }
      const updatedProduct = await toggleProductInWishlist(toggleProductProps)
      if (isRemoveAll) {
        isSelectAll.value = false
        store.commit(UPDATE_SELECT_COUNT, 0)
      }
      if (Array.isArray(updatedProduct)) {
        handleBulkUpdate(updatedProduct, index)
      } else {
        handleSingleUpdate(updatedProduct, product, index, groupIndex)
      }
      loadWishlists(isCreateWishlist)
    }

    const conflictedProductIndices = ref({})

    const updateProductAfterConflict = updatedProduct => {
      const { groupIndex, index } = conflictedProductIndices.value
      if (groupIndex !== undefined && index !== undefined) {
        updatedProduct.isInCart = true
        Vue.set(products.value[groupIndex].products, index, updatedProduct)
        handleReplaceCartProduct()
        analytics.track(
          constants.TRACKS.ACTIONS.ADD_TO_CART,
          formatObject(updatedProduct),
        )
        analytics.track(
          constants.TRACKS.ACTIONS.RETAILER_ADDS_TO_CART,
          formatObject({ collectionName: collectionData.value?.collectionName }),
        )
        conflictedProductIndices.value = {}
      }
    }

    const reactHandleCartActionClick = async (product, index, groupIndex) => {
      const updatedProduct = await handleCartActionClick({
        product,
        location: 'shopItems',
        conflictResolveCallback: 'updateProductAfterConflict',
      })
      if (updatedProduct) {
        Vue.set(products.value[groupIndex].products, index, updatedProduct)
        handleReplaceCartProduct()
        analytics.track(
          constants.TRACKS.ACTIONS.RETAILER_ADDS_TO_CART,
          formatObject({ collectionName: collectionData.value?.collectionName }),
        )
      } else {
        conflictedProductIndices.value = {
          groupIndex,
          index,
        }
      }
    }

    const selectedProductIdsCount = ref(0)

    const updateMultipleProductsAfterConflict = updatedProducts => {
      if (Array.isArray(updatedProducts)) {
        markItemsAsInCart(updatedProducts)
        store.commit(UPDATE_CART_ITEMS_COUNT, selectedProductIdsCount.value)
        analytics.track(
          constants.TRACKS.ACTIONS.RETAILER_ADDS_SELECTED_ITEMS_TO_CART,
          formatObject({
            collectionName: collectionData.value?.collectionName,
            noOfProductsSelected: updatedProducts?.length,
          }),
        )
      }
    }

    const addMultipleItemsCartActionClick = async ({
      selectedProductsIds,
      product,
    }) => {
      selectedProductIdsCount.value = selectedProductsIds?.length
      const updatedProduct = await addMultipleItemsInCart({
        selectedProductsIds,
        product,
        location: 'shopItems',
        conflictResolveCallback: 'updateMultipleProductsAfterConflict',
        skipUpdateCartCount: true,
      })
      isSelectAll.value = false
      store.commit(UPDATE_SELECT_COUNT, 0)
      if (Array.isArray(updatedProduct)) {
        analytics.track(
          constants.TRACKS.ACTIONS.RETAILER_ADDS_SELECTED_ITEMS_TO_CART,
          formatObject({
            collectionName: collectionData.value?.collectionName,
            noOfProductsSelected: updatedProduct?.length,
          }),
        )
      }
      handleReplaceCartProduct()
    }

    const handleReplaceCartProduct = async () => {
      const replacingProduct = await root.$store.getters.getReplaceCarProduct
      if (replacingProduct) {
        root.$router.push({ name: 'v2-checkout' })
      }
    }

    const {
      totalProducts,
      isWspHidden,
      isBrandNotAccessible,
      connectionRequestStatus,
      brandId,
      filterByAvailabilityDates,
    } = useShopUi()

    const { products, fetchProducts } = useShopRemoteData()

    const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility()

    // filtering
    const filters = ref({
      search: '',
      page: 0,
      limit: PRODUCTS_LIMIT,
    })
    const filterOptions = ref({})
    const filterSetup = ref(false)

    const inactiveCollection = ref(false)
    const groups = ref({})
    const defaultGroup = ref('None')
    const selectedGroup = ref(defaultGroup.value)

    const isFilterApplied = ref(false)
    const directRetailerId = ref(null)
    const isLoaded = ref(false)
    const showWishlistListModal = ref(false)

    const groupProducts = group => {
      selectedGroup.value = group
      isFilterApplied.value = true
      filters.value.page = 0
      fetchShopProducts()
      if (isRetailer.value) {
        analytics.track(
          constants.TRACKS.ACTIONS.RETAILER_GROUPS_PRODUCTS,
          formatObject({ selectedGroup: group }),
        )
      }
    }

    // eslint-disable-next-line no-unused-vars
    const getFetchParams = (_resetFilters = false, shipWishlist) => buildFetchParams({
      sortByValue: sortBy.value.value,
      filtersValue: filters.value,
      profileValue: profile.value,
      selectedWishlistIdValue: selectedWishlistId.value,
      skipWishlist: shipWishlist,
      directRetailerIdValue: directRetailerId.value,
      selectedGroupValue: selectedGroup.value,
      defaultGroupValue: defaultGroup.value,
      collectionId: props.collectionId,
    })

    const toggleRemoveAllBtn = val => {
      root.$store.commit(SET_IS_ENABLE_REMOVE_WISHLIST_BUTTON, val)
    }
    const indexes = ref([])
    const shipWishlist = false
    const isSelectAll = ref(false)

    const { onToggleSelectAll, isSelectedAllOnScreen } = useSelections(
      products,
      getFetchParams,
      indexes,
      shipWishlist,
      toggleRemoveAllBtn,
    )

    const forceToggleSelect = forceSelect => {
      onToggleSelectAll({
        forceSelect,
        selectedWishlistId: selectedWishlistId.value,
      })
    }

    const toggleSelectAllHandler = () => {
      isSelectAll.value = true
      onToggleSelectAll({
        selectEventTrack: () => analytics.track(
            MULTI_SELECT_PRODUCTS.USER_CLICKS_SELECT_ALL_PRODUCTS_IN_COLLECTION_PRODUCTS_PAGE,
            { collectionDetails: collectionData.value },
          ),
        unselectEvent: () => analytics.track(
            MULTI_SELECT_PRODUCTS.USER_CLICKS_DESELECT_ALL_PRODUCTS_IN_COLLECTION_PRODUCTS_PAGE,
            { collectionDetails: collectionData.value },
          ),
        selectedWishlistId: selectedWishlistId.value,
      })
    }

    let initialWholesalePriceMax
    let initialWholesalePriceMin
    let initialRetailPriceMax
    let initialRetailPriceMin
    let productsCount = 0

    const handleNewFilter = newFilter => {
      if (!newFilter || typeof newFilter !== 'object') return
      isFilterApplied.value = true
      filters.value = newFilter
      filters.value.page = 0
      stripUnchangedPriceFilters(filters.value, {
        initialWholesalePriceMax,
        initialWholesalePriceMin,
        initialRetailPriceMax,
        initialRetailPriceMin,
      })
    }

    const updateResultMetadata = (result, fetchParam) => {
      root.$store.commit(UPDATE_TOTAL_PRODUCTS_COUNT, result.count)
      collectionData.value = result
      if (!fetchParam.currency) {
        collectionBaseCurrency.value = result.products[0]?.products[0]?.currency
      }
      const meta = extractResultMetadata(result)
      if (meta.isCollectionExpired !== undefined) {
        inactiveCollection.value = meta.isCollectionExpired
      }
      connectionRequestStatus.value = meta.connectionRequestStatus
      isWspHidden.value = meta.isWspHidden
      isBrandNotAccessible.value = meta.isBrandNotAccessible
    }

    const updateProductsList = result => {
      if (result.page === 0) {
        productsCount = 0
      }
      productsCount = processGroupedProducts(result.products, productsCount)
      continueLoading.value = result.count > productsCount

      if (result.page >= 1) {
        products.value = mergePagedProducts(products.value, result.products)
      } else {
        products.value = result.products
      }
      totalProducts.value = result.count
      brandId.value = result.brandId
    }

    const trackFilterApplied = fetchParam => {
      if (!isFilterApplied.value) return
      analytics.track(
        isBrand.value
          ? constants.TRACKS.ACTIONS.BRAND_APPLIES_FILTER_CLN
          : constants.TRACKS.ACTIONS.RETAILER_APPLIES_FILTER_CLN,
        formatObject(fetchParam),
      )
    }

    const handleFetchError = err => applyProductsFetchError(err, {
      isCollectionUnavailable, continueLoading, products, totalProducts, isLoading, isLoaded,
    })

    // Wrapper Function for `fetchProducts` which can be triggered initially and upon changes of filters
    const fetchShopProducts = (newFilter, resetFilters = false) => new Promise(resolve => {
        isFetchingWishlists.value = true
        handleNewFilter(newFilter)
        const fetchParam = getFetchParams(resetFilters)
        if (filterSetup.value) updateFilter(fetchParam)

        latest(fetchProducts(fetchParam))
          .then(response => {
            const result = response.data.data
            updateResultMetadata(result, fetchParam)
            updateProductsList(result)
            if (!isFilterApplied.value) {
              trackCollectionOpenEvents(isBrand.value, result, collectionData.value)
            }
            isLoaded.value = true
            resolve(response)
            fetchQueryParams.value = fetchParam
          })
          .catch(err => {
            handleFetchError(err)
            // Settle the promise on failure (resolve null) so callers' .finally
            // / .then run instead of hanging forever. Resolving rather than
            // rejecting keeps the many fire-and-forget callers from producing
            // unhandled rejections (captured by Sentry). A superseded response
            // resolves STALE_RESPONSE so loadNextPage leaves the page alone.
            resolve(err === STALE_RESPONSE ? STALE_RESPONSE : null)
          })
          .finally(() => {
            isFetchingWishlists.value = false
          })
        trackFilterApplied(fetchParam)
      })

    const filterByCurrency = (currency = '') => {
      if (!currency) {
        delete filters.value?.currency
      } else {
        filters.value.currency = currency
        filters.value.page = 0
      }
      fetchShopProducts()
    }

    const setWishlist = (wishlistId = '', isCreateWishlist = false) => {
      if (!wishlistId) {
        selectedWishlistId.value = ''
        if (wishlists.value?.length) {
          selectedWishlist.value = {
            name: 'None',
            _id: '',
            count: totalProducts,
          }
          selectedWishlistId.value = selectedWishlist.value._id
          if (isCreateWishlist) {
            fetchShopProducts()
          }
        }
        return
      }
      selectedWishlistId.value = !isBrand.value && wishlistId

      selectedWishlist.value = wishlists.value.find(
        wishlist => wishlist?._id === selectedWishlistId.value,
      )
    }

    const updateProductsByWishlistId = data => {
      if (shouldChangeWishlist.value && !isBrand.value) {
        setWishlist(data?.wishlistId)
      }
      shouldChangeWishlist.value = false
      isDisableCreateWishlist.value = false
      isSelectAll.value = false
      filters.value.page = 0
      fetchShopProducts()
    }

    watch(
      [sortBy],
      () => {
        if (filterSetup.value) {
          filters.value.page = 0
          fetchShopProducts()
        }
      },
      {
        deep: true,
      },
    )

    // update filter options when select a menu
    const updateFilter = async fetchParam => {
      if (directRetailerId.value) {
        fetchParam.retailerId = directRetailerId.value
      }
      const res = await root.$store.dispatch(FETCH_COLLECTION_PRODUCT_FILTERS, fetchParam)
      const filterMenus = res.data.data[0]
      const updates = applyFilterMenuUpdates(filterMenus, filterOptions.value, fetchParam, filterByAvailabilityDates.value)
      if (updates.updatedFilterOptions) {
        Object.assign(filterOptions.value, updates.updatedFilterOptions)
      }
      if (updates.updatedAvailabilityDates !== undefined) {
        filterByAvailabilityDates.value = updates.updatedAvailabilityDates
      }
      if (updates.groups !== null) {
        groups.value = updates.groups
      }
    }

    // setup filters
    const fetchShopFilters = async () => {
      const res = await root.$store.dispatch(FETCH_COLLECTION_PRODUCT_FILTERS, {
        collectionId: props.collectionId,
        isDemo: !!profile.value?.isDemo,
        retailerId: directRetailerId.value || null,
      })
      filterOptions.value = res.data.data[0]
      if (filterOptions.value) {
        filterOptions.value.colorEntries = buildColorEntries(filterOptions.value)
      }
      initializeFilterOptions(filterOptions.value, filters.value)
      if (!filterOptions.value) {
        getDirectRetailers()
        return
      }
      const derived = buildDerivedFilterState(filterOptions.value)
      filterByAvailabilityDates.value = derived.availabilityDates
      const priceBounds = applyDerivedFilterState(derived, filterOptions.value)
      groups.value = priceBounds.groups
      initialWholesalePriceMax = priceBounds.initialWholesalePriceMax
      initialWholesalePriceMin = priceBounds.initialWholesalePriceMin
      initialRetailPriceMax = priceBounds.initialRetailPriceMax
      initialRetailPriceMin = priceBounds.initialRetailPriceMin
      filterSetup.value = true
      getDirectRetailers()
    }

    if (!filterSetup.value) fetchShopFilters()

    // Build product→wishlists map by querying product IDs per wishlist
    const buildProductWishlistsMap = async wishlistsList => {
      const map = {}
      await Promise.all(
        wishlistsList.map(async wishlist => {
          try {
            const res = await store.dispatch(GET_PRODUCTS_IDS, {
              collectionId: props.collectionId,
              wishlistId: wishlist._id,
            })
            const productIds = res?.data?.data || []
            productIds.forEach(id => {
              if (!map[id]) map[id] = []
              if (!map[id].includes(wishlist._id)) {
                map[id].push(wishlist._id)
              }
            })
          } catch {
            // Silently skip wishlists that fail
          }
        }),
      )
      productWishlistsMap.value = { ...productWishlistsMap.value, ...map }
    }

    const loadWishlists = async (isCreateWishlist = false) => {
      await store
        .dispatch(FETCH_WISHLISTS, {
          force: true,
          collectionId: props.collectionId,
        })
        .then(resWishlists => {
          wishlists.value = resWishlists.filter(
            wishlist => !wishlist.isReadOnly,
          )
        })
      setWishlist(selectedWishlistId.value || '', isCreateWishlist)
      fetchShopProducts()
      // Build product-wishlists map after wishlists are loaded
      if (wishlists.value.length) {
        buildProductWishlistsMap(wishlists.value)
      }
    }

    // Load wishlists
    loadWishlists()

    const getDirectRetailers = async () => {
      const access = profile.value?.access || []
      if (isBrand.value && access.includes(BRAND_ACCESS.DIRECT)) {
        const directRetailers = await root.$store.dispatch(
          FETCH_DIRECT_RETAILERS,
        )
        if (directRetailers?.length) {
          hasDirectRetailers.value = true
        }
      }
    }

    // loading on scroll
    const isLoading = ref(false)
    const loadingIcon = ref(null)
    const continueLoading = ref(false)
    const scrollArea = ref(null)

    const isNearBottom = el => {
      const { scrollTop, scrollHeight, clientHeight } = el
      return scrollTop + clientHeight >= scrollHeight - 200
    }

    const loadNextPage = () => {
      isLoading.value = true
      // getFetchParams reads filters.value.page, so the page must be bumped
      // before the fetch. Roll it back if the fetch fails so a transient error
      // retries the same page on the next trigger instead of skipping it.
      filters.value.page++
      return fetchShopProducts()
        .then(response => {
          if (!response) {
            filters.value.page--
            return false
          }
          return response !== STALE_RESPONSE
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const onScrollLoader = () => {
      if (isLoading.value || !continueLoading.value || !scrollArea.value) return
      if (!isNearBottom(scrollArea.value)) return
      loadNextPage()
    }

    // When the loaded products don't overflow the scroll area (e.g. the page is
    // zoomed out, or the first page is shorter than the viewport), no scroll
    // event can ever fire, so onScrollLoader never triggers and the "Loading
    // more..." spinner sits there forever. Keep loading pages until the content
    // overflows or there's nothing left to load.
    const ensureViewportFilled = () => {
      if (isLoading.value || !continueLoading.value || !scrollArea.value) return
      const el = scrollArea.value
      if (el.scrollHeight <= el.clientHeight) {
        loadNextPage().then(success => {
          // Only keep filling if the fetch succeeded; otherwise a persistent
          // error would recurse forever (page rolls back, continueLoading and
          // scrollHeight stay unchanged), hammering the server.
          if (success) {
            Vue.nextTick(ensureViewportFilled)
          }
        })
      }
    }

    const searchProducts = debounce(() => {
      isFilterApplied.value = true
      filters.value.page = 0
      fetchShopProducts()
    }, 1000)

    const closeSideBar = () => {
      mqShallShowLeftSidebar.value = false
    }

    const onSelectAllMoveToWishlist = ({
      wishlistId,
      wishlistName,
      isCreateWishlist,
      fullIdsList,
    }) => {
      const payload = {
        deselectedProducts: deselectedProducts.value?.map(p => p._id),
      }
      if (fullIdsList) {
        payload.products = fullIdsList.map(productId => ({ productId }))
      } else {
        fetchQueryParams.value.isSelectedAllProducts = true
      }
      let filterQueries = {}
      fetchQueryParams.value.collectionId = props.collectionId
      if (selectedWishlist.value?._id) {
        fetchQueryParams.value.wishlistId = selectedWishlist.value._id
      }
      filterQueries = fetchQueryParams.value
      delete filterQueries.page
      delete filterQueries.limit
      if (isCreateWishlist) {
        payload.name = wishlistName
      } else {
        payload.wishlistId = wishlistId
      }
      store
        .dispatch(ADD_PRODUCT_TO_WISHLIST, {
          payload,
          queryParams: filterQueries,
        })
        .then(res => {
          apiToastSuccess(res.data.message)
          indexes.value = []
          store.commit(UPDATE_SELECT_COUNT, 0)
          store.commit(UPDATE_IS_SELECTED_ALL_PRODUCTS, false)
          deselectedProducts.value = []
          loadWishlists(isCreateWishlist)
          store.commit(SHOW_WISHLIST_NAV_INDICATOR)
        })
        .catch(() => {})
        .finally(() => {
          isSelectAll.value = false
        })
    }

    // Attach scroll listener when content is loaded and scroll area is in the DOM
    watch(isLoaded, val => {
      if (val) {
        Vue.nextTick(() => {
          if (scrollArea.value) {
            scrollArea.value.addEventListener('scroll', onScrollLoader)
          }
          ensureViewportFilled()
        })
      }
    })

    // Re-check after every fetch resolves: once the latest page is rendered the
    // content may still not overflow the scroll area (zoomed out / large screen),
    // so keep filling until it does or there's nothing more to load.
    watch(products, () => {
      Vue.nextTick(ensureViewportFilled)
    })

    // Zooming out fires a resize and grows the visible area without producing a
    // scroll event, so re-check whether more pages are needed.
    const onWindowResize = () => {
      Vue.nextTick(ensureViewportFilled)
    }

    // Stable reference so removeEventListener can actually detach it on unmount.
    const onPopState = () => {
      root.$store.commit(SET_REPLACE_CART_PRODUCT, null)
    }

    onMounted(() => {
      window.addEventListener('popstate', onPopState)
      window.addEventListener('resize', onWindowResize)
    })

    onUnmounted(() => {
      if (scrollArea.value) {
        scrollArea.value.removeEventListener('scroll', onScrollLoader)
      }
      window.removeEventListener('popstate', onPopState)
      window.removeEventListener('resize', onWindowResize)
      root.$store.commit(UPDATE_SELECTED_DIRECT_RETAILER, null)
    })

    return {
      isLoading,
      isLoaded,
      filters,
      filterOptions,
      filterSetup,
      sortBy,
      sortByOptions,
      loadingIcon,
      continueLoading,
      deselectedProducts,
      collectionBaseCurrency,
      isDisableCreateWishlist,
      totalProducts,
      fetchShopProducts,
      reactToggleProductInWishlist,
      reactHandleCartActionClick,
      addMultipleItemsCartActionClick,
      isWspHidden,
      isBrandNotAccessible,
      connectionRequestStatus,
      brandId,
      filterByAvailabilityDates,
      groups,
      selectedGroup,
      onClickSelectWishlist,
      defaultGroup,
      groupProducts,
      searchProducts,
      products,
      inactiveCollection,
      mqShallShowLeftSidebar,
      closeSideBar,
      formatCurrency,
      CONNECTION_REQUEST_STATUS,
      isRetailer,
      isBrand,
      directRetailerId,
      updateProductsByWishlistId,
      loadWishlists,
      setWishlist,
      selectedWishlistId,
      selectedWishlist,
      wishlists,
      productWishlistsMap,
      onSelectAllMoveToWishlist,
      collectionData,
      isCollectionUnavailable,
      filterByCurrency,
      isSelectedAllOnScreen,
      onToggleSelectAll: toggleSelectAllHandler,
      indexes,
      forceToggleSelect,
      updateProductAfterConflict,
      updateMultipleProductsAfterConflict,
      isFetchingWishlists,
      shouldChangeWishlist,
      showWishlistListModal,
      scrollArea,
    }
  },
  data() {
    return {
      PRODUCTS_VIEW,
      itemViewClass: PRODUCTS_VIEW.GRID_VIEW,
      selectedSortBy: null,
      searchBarPlaceholderText,
      showDownloadModal: false,
      productList: [],
      groupIndexes: [],
      cartProducts: [],
      isLoadingCart: false,
      isBrandCartUpdated: 1,
      brandCartRetailerId: null,
      brandCartProductIds: [],
      brandCartModalId: 'brand-add-to-cart-modal',
      showBrandCartModal: false,
      selectedAvailabilityDate: [],
      isGuest: false,
      modalProductId: null,
      // True from "Add to order" (brand) / "Add to cart" (retailer) click
      // until the products have been fetched + (brand) modal opened.
      // Drives the full-screen overlay loader so the page isn't visually
      // idle while large selections (200+ products) are being resolved.
      isProcessingAddAction: false,
    }
  },
  watch: {
    indexes(val) {
      this.groupIndexes = this.products.flatMap((group, groupIndex) => group.products
        .filter(product => val.includes(product._id))
        .map(product => ({
          groupIndex,
          productId: product._id,
        })))
      this.productList = this.products.flatMap(group => group.products.filter(product => val.includes(product._id)))
    },
  },
  computed: {
    // KP-3462: when ON, render the space-reclaiming layout (full-width toolbar
    // with the title inside, denser grid). OFF = original header + in-content
    // controls bar. Gated behind the B2B Flagsmith flag.
    isKp3462Layout() {
      const hasFeature = this.$store?.getters?.['flags/hasFeature']
      return typeof hasFeature === 'function'
        ? hasFeature(FLAG_CONSTANTS.KP_3462_COLLAPSIBLE_SIDEBAR)
        : false
    },
    isShowWsp() {
      return !(
        this.isWspHidden
        && this.connectionRequestStatus !== CONNECTION_REQUEST_STATUS.APPROVED
      )
    },
    hasFilterOptions() {
      return Object.keys(this.filterOptions)?.length > 2
    },
    shouldShowEmptyWishlistMsg() {
      return (
        this.selectedWishlist?._id
        && this.totalProducts <= 0
        && !this.isFetchingWishlists
        && !this.filters.search
      )
    },
    ...mapState({
      profileData: state => state.auth.profileData,
      selectCount: state => state.shop.selectCount,
      isEnableRemoveWishlistButton: state => state.wishlist.isEnableRemoveWishlistButton,
      duplicateOrderData: state => state.shop.duplicateOrderData,
      replacingProduct: state => state.shop.replaceCartProduct,
    }),
    isOrderEnableInShop() {
      return this.isRetailer && !this.inactiveCollection && !this.isBrandNotAccessible
    },
    wishlistBtnLabel() {
      if (!this.selectedWishlist) return 'Select a wishlist'
      if (this.selectedWishlist._id === '') {
        return `${this.selectedWishlist.name} (${this.totalProducts ?? 0})`
      }
      return `${textTruncate(this.selectedWishlist.name, true, 20, 15)} (${
        this.selectedWishlist.count || 0
      })`
    },
    groupOptions() {
      if (!this.groups || !Array.isArray(this.groups)) return ['None']
      return this.groups
    },
    selectedDirectRetailer() {
      return this.$store.getters.selectedDirectRetailer
    },
    downloadRetailerId() {
      return this.directRetailerId
        || (this.selectedDirectRetailer && this.selectedDirectRetailer.retailerId)
        || this.$route.query.retailerId
        || null
    },
  },
  created() {
    this.$root.$refs.shopItems = this
    store.commit(UPDATE_SELECT_TYPE, MULTI_SELECT_PAGES.SHOP)
    this.selectedSortBy = this.sortByOptions[0]
    if (this.isRetailer) {
      analytics.track(constants.TRACKS.PAGES.SHOP)
    }
  },
  mounted() {
    this.handleDuplicateOrderData()
  },
  destroyed() {
    store.commit(UPDATE_SELECT_COUNT, 0)
    store.commit(UPDATE_IS_SELECTED_ALL_PRODUCTS, false)
    analytics.track(
      this.isBrand
        ? constants.TRACKS.ACTIONS.BRAND_END_VIEWING_COLLECTION
        : constants.TRACKS.ACTIONS.RETAILER_END_VIEWING_COLLECTION,
      this.collectionData,
    )
  },
  methods: {
    cancelReplace() {
      this.$store.commit(SET_REPLACE_CART_PRODUCT, null)
      this.$router.push({ name: 'v2-checkout' })
    },
    getProductActions(product) {
      const actions = []
      if (this.isRetailer) {
        actions.push({
          key: 'cart',
          label: product.isInCart ? 'View In Cart' : 'Add to Cart',
          variant: product.isInCart ? 'secondary' : 'primary',
          disabled: product.price === 'N/A',
        })
      }
      return actions
    },
    onProductAction({
 action, product, index, groupIndex,
}) {
      if (action === 'cart') {
        this.reactHandleCartActionClick(product, index, groupIndex)
      }
    },
    onCheckboxChange(product, isChecked) {
      const productId = product._id
      if (isChecked) {
        if (!this.indexes.includes(productId)) {
          this.indexes = [...this.indexes, productId]
          analytics.track(
            MULTI_SELECT_PRODUCTS.USER_SELECTS_PRODUCT_BY_CHECKBOX_IN_COLLECTION_PRODUCTS_PAGE,
            { product },
          )
        }
      } else {
        this.indexes = this.indexes.filter(id => id !== productId)
        analytics.track(
          MULTI_SELECT_PRODUCTS.USER_DESELECTS_PRODUCT_BY_CHECKBOX_IN_COLLECTION_PRODUCTS_PAGE,
          { product },
        )
      }
    },
    goBack() {
      this.$router.back()
    },
    onClickProduct(product) {
      this.modalProductId = product._id
    },
    onProductDeleted({ productId }) {
      // Cheap state cleanup first so it always succeeds regardless of grid shape.
      this.modalProductId = null
      this.productList = this.productList.filter(p => p._id !== productId)
      this.indexes = this.indexes.filter(id => id !== productId)
      // Drop the deleted product from the rendered grid (composition-API ref `products`,
      // grouped as [{ _id, products: [...] }]) so the row vanishes without a refetch.
      // Defensive guard: in some test environments the ref isn't auto-unwrapped to an
      // array, so the iteration is a no-op rather than throwing.
      if (Array.isArray(this.products)) {
        this.products.forEach(group => {
          if (Array.isArray(group?.products)) {
            const idx = group.products.findIndex(p => p._id === productId)
            if (idx !== -1) group.products.splice(idx, 1)
          }
        })
      }
    },
    onGridViewClick() {
      this.itemViewClass = PRODUCTS_VIEW.GRID_VIEW
      analytics.track(
        this.isBrand
          ? constants.TRACKS.ACTIONS.BRAND_SELECTS_GENERAL_VIEW
          : constants.TRACKS.ACTIONS.RETAILER_SELECTS_GENERAL_VIEW,
        formatObject({ noOfProductsInCollection: this.totalProducts }),
      )
    },
    onCompactViewClick() {
      this.itemViewClass = PRODUCTS_VIEW.COMPACT_GRID_VIEW
      analytics.track(
        this.isBrand
          ? constants.TRACKS.ACTIONS.BRAND_SELECTS_COMPACT_VIEW
          : constants.TRACKS.ACTIONS.RETAILER_SELECTS_COMPACT_VIEW,
        formatObject({ noOfProductsInCollection: this.totalProducts }),
      )
    },
    onCompactToggleClick() {
      if (this.itemViewClass === PRODUCTS_VIEW.GRID_VIEW) {
        this.onCompactViewClick()
      } else {
        this.onGridViewClick()
      }
    },
    filterByDate() {
      const availabilityDates = []
      this.selectedAvailabilityDate.forEach(index => {
        if (this.filterByAvailabilityDates[index]) {
          availabilityDates.push(this.filterByAvailabilityDates[index])
        }
      })
      this.filters.availabilityDates = availabilityDates
      this.filters.page = 0
      this.fetchShopProducts()
    },
    onSortChange(val) {
      const opt = this.sortByOptions.find(o => o.value === val)
      if (opt) {
        this.selectedSortBy = opt
        this.sortBy = opt
      }
    },
    // Per-product wishlist dropdown methods
    // eslint-disable-next-line no-unused-vars
    getWishlistsToShow(_product) {
      if (this.directRetailerId) {
        return this.wishlists.filter(
          wishlist => this.directRetailerId === wishlist.userId,
        )
      }
      return this.wishlists
    },
    getProductWishlists(product) {
      // Use client-side map (API doesn't return product.wishlists)
      return this.productWishlistsMap[product._id] || product.wishlists || []
    },
    getProductWishlistsCount(product) {
      const pWishlists = this.getProductWishlists(product)
      if (this.directRetailerId) {
        const filtered = pWishlists
          .map(id => this.wishlists.find(w => w._id === id))
          .filter(Boolean)
        return filtered.length
      }
      return pWishlists.length
    },
    onSelectWishlistOnProduct(product, pIndex, gIndex, wishlist) {
      const pWishlists = this.getProductWishlists(product)
      let action = this.isRetailer
        ? wishlistAction.ADD_TO_WISHLIST
        : wishlistAction.ADD_BY_BRAND
      const isInWishlist = pWishlists.includes(wishlist._id)
      if (isInWishlist) {
        action = wishlistAction.REMOVE_FROM_WISHLIST
      }
      // Update client-side map immediately for instant UI feedback
      const updated = isInWishlist
        ? pWishlists.filter(id => id !== wishlist._id)
        : [...pWishlists, wishlist._id]
      this.$set(this.productWishlistsMap, product._id, updated)
      this.reactToggleProductInWishlist({
        product,
        index: pIndex,
        groupIndex: gIndex,
        action,
        wishlistId: wishlist._id,
        wishlistName: wishlist.name,
        wishlist,
      })
    },
    onCreateWishlist(product, pIndex, gIndex) {
      this.productList.push(product)
      this.groupIndexes.push({ groupIndex: gIndex, productId: product._id })
      store.commit(SET_IS_ENABLE_CREATE_WISHLIST, true)
      store.commit(SET_IS_CREATE_WISHLIST_BTN_DISABLED, false)
      this.showWishlistListModal = true
    },
    onToggleWishlistDropdown(product, isOpened) {
      product.isOpenedDropdown = isOpened
    },
    // WishlistList modal callbacks
    moveAllToWishList(data) {
      const { wishlistId, wishlistName, isCreateWishlist } = data || {}
      if (this.indexes.length) {
        this.onSelectAllMoveToWishlist({
          wishlistId,
          wishlistName,
          isCreateWishlist,
          fullIdsList: this.indexes,
        })
      } else {
        const productIds = this.productList.map(p => p._id)
        this.productList.forEach((product, idx) => {
          const { groupIndex } = this.groupIndexes[idx] || {}
          const action = this.isRetailer
            ? wishlistAction.ADD_TO_WISHLIST
            : wishlistAction.ADD_BY_BRAND
          this.reactToggleProductInWishlist({
            product,
            index: productIds,
            groupIndex,
            action,
            wishlistId,
            wishlistName,
            isCreateWishlist,
          })
        })
      }
      this.productList = []
      this.groupIndexes = []
      if (this.isRetailer) {
        this.updateProductsByWishlistId({ wishlistId })
      }
    },
    handleUnsetWishlist(wishlistData) {
      if (this.isRetailer) {
        this.updateProductsByWishlistId(wishlistData)
      }
    },
    handleShouldChangeWishlist() {
      this.shouldChangeWishlist = false
      this.isDisableCreateWishlist = false
    },
    // Bottom bar actions
    onSelectOrCreateWishlist() {
      store.commit(SET_IS_CREATE_WISHLIST_BTN_DISABLED, false)
      this.showWishlistListModal = true
      analytics.track(
        this.isBrand
          ? MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_ADD_TO_SELECTION
          : MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_ADD_TO_WISHLIST,
        { selectedProductsCount: this.selectCount, currentPage: 'shop' },
      )
    },
    onRemoveFromWishlist() {
      // Remove selected products from wishlists
      this.reactToggleProductInWishlist({
        product: null,
        index: this.indexes,
        groupIndex: null,
        action: wishlistAction.REMOVE_FROM_WISHLIST,
        wishlistId: this.selectedWishlistId,
        wishlistName: this.selectedWishlist?.name,
        wishlist: this.selectedWishlist,
      })
      this.forceToggleSelect(false)
      analytics.track(
        MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_REMOVE_FROM_WISHLIST,
        { currentPage: 'shop' },
      )
    },
    async onMoveAllToCart() {
      // Same rationale as onAddToOrder: addMultipleItemsInCart is async and
      // can take several seconds for a 200+ product selection. Without the
      // overlay the page sat visually idle between click and completion.
      // Was previously fire-and-forget; awaiting is safe — only the toast
      // / analytics order changes (now they fire after success rather than
      // immediately).
      this.isProcessingAddAction = true
      try {
        await this.addMultipleItemsCartActionClick({
          product: this.productList,
          selectedProductsIds: this.indexes,
          groupIndexes: this.groupIndexes,
        })
        this.forceToggleSelect(false)
        analytics.track(
          MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_ADD_TO_CART,
          { selectedProductsCount: this.selectCount, currentPage: 'shop' },
        )
      } finally {
        this.isProcessingAddAction = false
      }
    },
    async onDownloadSelectedProducts() {
      try {
        const payload = {
          downloadType: DOWNLOAD_TYPES.COLLECTION_EXPORT.VALUE,
          respectiveId: this.collectionId,
          fileType: FILE_FORMATS.XLSX,
          productIds: this.indexes,
        }
        if (this.directRetailerId) {
          payload.retailerId = this.directRetailerId
        }
        const params = { isOrderSheetDownload: true }
        const res = await store.dispatch(DOWNLOAD_COLLECTION, { payload, params })
        apiToastSuccess(res.data.message)
        store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
        this.forceToggleSelect(false)
      } catch (err) {
        // error toast handled by axios interceptor
      }
      analytics.track(
        MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_DOWNLOAD_PRODUCTS,
        { selectedProductsCount: this.selectCount, currentPage: 'shop' },
      )
    },
    async onAddToOrder() {
      // Show the full-screen overlay immediately so the page isn't visually
      // idle while GET_PRODUCTS_BY_IDS resolves — that fetch can take 5+
      // seconds for a 200+ product selection and otherwise the user has
      // no feedback that the click registered.
      this.isProcessingAddAction = true
      try {
        await this.getFullSelectedProducts()
        this.forceToggleSelect(false)
        analytics.track(TRACKS.ACTIONS.BRAND_CLICKS_ON_ADD_TO_ORDER, {
          product_count: this.cartProducts.length,
          product_ids: this.cartProducts.map(p => p._id),
        })
        this.showBrandCartModal = true
      } finally {
        this.isProcessingAddAction = false
      }
    },
    // Used by useCartTotal.js (onCartImportSuccess) — must be a method reference, not computed
    async getCartProductsByBrand(retailerId, { fetchFromCart } = {}) {
      const effectiveRetailerId = retailerId || this.brandCartRetailerId
      if (!fetchFromCart) {
        await this.getFullSelectedProducts({
          retailerId: effectiveRetailerId,
          productIds: this.cartProducts.map(p => p._id),
        })
        return
      }
      // After import, fetch from the cart endpoint so quantities are included.
      this.isLoadingCart = true
      try {
        const res = await store.dispatch(FETCH_CART_PRODUCTS, { retailerId: effectiveRetailerId })
        const cartData = extractCartProductsFromResponse(res)
        if (cartData) {
          this.cartProducts = cartData
        }
      } catch (error) {
        console.error('Error fetching cart products:', error)
      } finally {
        this.isLoadingCart = false
      }
    },
    async getFullSelectedProducts(params = {}) {
      this.isLoadingCart = true
      const resolved = resolveFullSelectedProductsParams(params, this.indexes)
      this.brandCartRetailerId = resolved.retailerId
      this.brandCartProductIds = resolved.productIds
      // Captured before the refetch — on a retailer switch the response carries
      // fresh pricing but DEFAULT quantities, which _prepareBrandCartProducts
      // reconciles.
      const previousProducts = this.cartProducts
      try {
        const res = await store.dispatch(GET_PRODUCTS_BY_IDS, {
          payload: { productIds: resolved.resolvedProductIds, retailerId: resolved.retailerId },
        })
        const result = processFullSelectedProductsResult(res?.data?.data, this.isBrand)
        this.cartProducts = this._prepareBrandCartProducts(result.cartProducts, {
          previousProducts,
          retailerId: resolved.retailerId,
        })
        this._applyBrandCartResultFlags(result)
      } catch (error) {
        console.error('Error fetching products:', error)
      } finally {
        this.isLoadingCart = false
      }
    },
    // Reconcile freshly fetched brand-cart products: restore the user's edited
    // quantities (lost on refetch) and stamp the retailer discount so the live
    // line totals are discounted. See _syncBrandCartValues / _applyRetailerDiscount.
    _prepareBrandCartProducts(products, { previousProducts, retailerId }) {
      if (previousProducts?.length && products?.length) {
        this._syncBrandCartValues(products, previousProducts)
      }
      this._applyRetailerDiscount(products, retailerId)
      return products
    },
    _applyBrandCartResultFlags(result) {
      if (result.shouldHideModal) {
        this.showBrandCartModal = false
      }
      if (result.warningMessage) {
        apiToastWarning(result.warningMessage)
      }
    },
    onCloseAddToCartModal() {
      this.showBrandCartModal = false
      this.cartProducts = []
      this.isBrandCartUpdated++
    },
    async handleDuplicateOrderData() {
      if (!this.isBrand || !this.duplicateOrderData) {
        return
      }

      const { products, collectionId, orderId } = this.duplicateOrderData

      if (collectionId !== this.collectionId) {
        return
      }

      const productIds = products.map(p => p.productId || p._id)

      if (!productIds.length) {
        this.$store.commit(CLEAR_DUPLICATE_ORDER_DATA)
        return
      }

      // Store order products temporarily to sync quantities later
      const orderProducts = products

      // Fetch latest product data by IDs
      await this.getFullSelectedProducts({ productIds })

      // Sync quantities from order products to fetched products
      this._syncBrandCartValues(this.cartProducts, orderProducts)

      this.$store.commit(CLEAR_DUPLICATE_ORDER_DATA)

      if (this.cartProducts.length > 0) {
        analytics.track(constants.TRACKS.ACTIONS.SINGLE_ORDER.BRAND_DUPLICATE_ORDER_SUCCESS, {
          orderId,
          collectionId: this.collectionId,
          productCount: this.cartProducts.length,
        })
      }

      this.showBrandCartModal = true
    },
    _syncBrandCartValues(data, orderProducts) {
      data.forEach(newProduct => {
        const matchingOrderProduct = orderProducts.find(
          cp => (cp.productId || cp._id) === newProduct._id,
        )
        if (!matchingOrderProduct) return

        if (newProduct.items && matchingOrderProduct.items) {
          newProduct.items.forEach(newItem => {
            matchingOrderProduct.items.forEach(orderItem => {
              const orderItemSku = orderItem.sku || orderItem.skuId
              if (orderItemSku === newItem.sku) {
                newItem.value = orderItem.value
              }
            })
          })
        }
        if (matchingOrderProduct.quantity !== undefined) {
          newProduct.quantity = matchingOrderProduct.quantity
        }
      })
    },
    // Stamp the selected retailer's discount multiplier onto each product so
    // the cart's live line totals reflect it. The catalog response carries a
    // per-unit discountedPrice but NOT the discount multiplier that
    // getProductAndCartTotals needs to discount line totals — without this a
    // 50%-off product still totals at full price (e.g. 10 × 2000 = 20000
    // instead of 10000). Mirrors what order placement does server-side.
    _applyRetailerDiscount(products, retailerId) {
      if (!retailerId || !products?.length) return
      const discount = this._getRetailerDiscount(retailerId)
      if (!discount) return
      products.forEach(product => {
        product.discount = discount
      })
    },
    _getRetailerDiscount(retailerId) {
      const selected = this.selectedDirectRetailer
      if (selected?.retailerId === retailerId && selected.discount) {
        return selected.discount
      }
      const retailers = store.state.connections?.directRetailers || []
      return retailers.find(r => r.retailerId === retailerId)?.discount || null
    },
    setDirectRetailerInfo(retailerId = null) {
      this.directRetailerId = retailerId
      this.filters.page = 0
      this.fetchShopProducts()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/v2/_glass-mixins.scss';

.v2-collection-products-page {
  height: calc(100vh - var(--v2-space-6) * 2);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // Full-screen overlay shown while bulk Add-to-order / Add-to-cart resolves.
  // z-index sits above the multi-select bar (z-index ~5000 in design system)
  // but below modals (~9000) so when the modal opens on top it eclipses
  // the spinner naturally. position: fixed escapes any ancestor transform/
  // overflow context. Backdrop is dimmed so the spinner reads clearly
  // against busy product grids; click-blocking prevents duplicate fires
  // from double-clicks during the in-flight request.
  &__add-action-overlay {
    position: fixed;
    inset: 0;
    z-index: 7000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
  }

  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    flex-shrink: 0;
  }

  // ===== KP-3462: space-reclaiming layout (flag ON only) ====================
  // Every override is scoped to the --kp3462 root modifier so the OFF path
  // (original header + in-content controls bar + 220px grid) is untouched.
  &--kp3462 {
    // Full-width toolbar: title + controls span both ends, never wraps.
    // Keeps the page's original flat glass fill (v2-glass-panel-flat from the
    // base &__controls rule) — products scroll in a contained area below it, so
    // a translucent bar never shows them through.
    .v2-collection-products-page__controls {
      flex-wrap: nowrap;
      margin-bottom: 1.5rem;
    }

    // Back + collection name inline at the left of the toolbar.
    .v2-collection-products-page__header-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 0 0 280px;
      min-width: 0;
    }

    .v2-collection-products-page__title-text {
      max-width: 16rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .v2-collection-products-page__left-controls {
      flex-wrap: nowrap;
      min-width: 0;

      // Only the search field flexes; buttons/dropdowns keep their size so
      // labels never clip when the bar is tight.
      > *:not(.v2-collection-products-page__search) {
        flex-shrink: 0;
      }
    }

    .v2-collection-products-page__right-controls {
      flex-shrink: 0;
    }

    .v2-collection-products-page__search {
      flex: 1 1 180px;
      min-width: 0;

      ::v-deep .v2-input {
        height: 38px;
      }
    }

    // Denser grid so more columns fit in the reclaimed iframe width.
    .v2-collection-products-page__grid {
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));

      &--compact {
        grid-template-columns: repeat(auto-fill, minmax(125px, 1fr));
      }
    }

    // Thin, transparent scrollbars on the filter sidebar and the product
    // scroll-area (no white track; a faint thumb that darkens slightly on
    // hover) so they don't read as heavy default browser scrollbars.
    .v2-collection-products-page__sidebar,
    .v2-collection-products-page__scroll-area {
      scrollbar-width: thin;
      scrollbar-color: var(--v2-glass-stroke) transparent;

      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-track {
        background: transparent !important;
      }
      &::-webkit-scrollbar-thumb {
        background: var(--v2-glass-stroke);
        border-radius: 999px;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: var(--v2-glass-stroke-strong);
      }
    }
  }

  &__back-btn {
    background: none;
    border: 1px solid var(--v2-border);
    border-radius: 0.5rem;
    padding: 0.375rem;
    cursor: pointer;
    color: var(--v2-foreground);
    display: flex;
    align-items: center;

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__header-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
  }

  &__replace-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-4);
    background: #fef3cd;
    border: 1px solid #ffc107;
    padding: var(--v2-space-3) var(--v2-space-4);
    border-radius: var(--v2-radius-lg);
    margin-bottom: var(--v2-space-4);
    flex-shrink: 0;
  }

  &__replace-banner-text {
    font-size: 0.875rem;
    color: var(--v2-foreground);
  }

  // Lite-mode "view as retailer" banner. Replaces the prior bold violet
  // gradient with a translucent glass surface that fits the rest of the V2
  // design. Subtle violet tint + accent stripe keeps the impersonation cue
  // visible without dominating the page.
  &__retailer-banner {
    position: relative;
    background: var(--v2-glass-surface-2);
    backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
    border: 1px solid var(--v2-glass-stroke);
    border-left: 3px solid var(--v2-primary);
    padding: var(--v2-space-2) var(--v2-space-4);
    border-radius: var(--v2-radius-md);
    margin-bottom: var(--v2-space-4);
    flex-shrink: 0;
  }

  &__main {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__retailer-banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-4);
  }

  &__retailer-banner-text {
    color: var(--v2-muted-foreground);
    font-size: 0.8125rem;
    font-weight: 500;

    strong {
      color: var(--v2-foreground);
      font-weight: 600;
    }
  }

  &__retailer-banner-button {
    // Use the outline button's default v2 styling — no white override now
    // that the banner is light.
  }

  &__sidebar {
    width: 280px;
    flex-shrink: 0;
    margin-right: var(--v2-space-4);
    overflow-y: auto;

    // Skin the legacy ShopFilter (src/views/apps/shop/ShopFilter.vue) with
    // glass styling without modifying the component itself. ShopFilter
    // renders a bootstrap-vue <b-card> inside `.sidebar-shop`; we target
    // that .card via deep selectors so the constraint "do not modify legacy
    // components" is preserved while the surface still reads as v2 glass.
    ::v-deep .sidebar-shop > .card {
      background: var(--v2-glass-surface-1);
      backdrop-filter: blur(var(--v2-glass-blur-md)) saturate(180%);
      border: 1px solid var(--v2-glass-stroke);
      border-radius: var(--v2-radius-lg);
      box-shadow: var(--v2-glass-shadow);
    }

    ::v-deep .card-body {
      background: transparent;
    }

    // Skin bootstrap-vue's b-form-checkbox (rendered as .custom-checkbox /
    // .custom-control-label::before) with glass surfaces so the legacy
    // ShopFilter checkboxes match V2Checkbox visually.
    // Match V2Checkbox recipe — surface-2 + glass-stroke-strong for
    // visible translucent box, solid violet on check. No `backdrop-filter`
    // (filter sidebar has many checkboxes; per-checkbox blur on a 16px
    // box is GPU-expensive for ~zero visual benefit).
    ::v-deep .custom-control-label::before {
      background: var(--v2-glass-surface-2);
      border: 1.5px solid var(--v2-glass-stroke-strong);
    }

    ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
      background: var(--v2-primary);
      border-color: var(--v2-primary);
      backdrop-filter: none;
    }

    ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
      border-color: var(--v2-glass-stroke-strong);
    }

    // Force long unbreakable strings (e.g. image URLs in the Sub Lines
    // filter) to wrap inside the sidebar instead of overflowing the card.
    // Clamps to 2 lines by default; on hover/focus the full label expands
    // — a CSS-only equivalent of "show more / show less" since the legacy
    // ShopFilter component renders these labels and we cannot modify it.
    ::v-deep .custom-checkbox {
      min-width: 0;
    }

    ::v-deep .custom-control-label {
      word-break: break-word;
      overflow-wrap: anywhere;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      cursor: pointer;
      transition: -webkit-line-clamp 0.15s ease;
    }

    ::v-deep .custom-control:hover .custom-control-label,
    ::v-deep .custom-control:focus-within .custom-control-label {
      -webkit-line-clamp: unset;
    }

    ::v-deep .filter-title {
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
    }

    ::v-deep .read-more {
      display: inline-block;
      margin-bottom: 0.25rem;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  &__scroll-area {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding-right: var(--v2-space-2);
  }

  &__controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-4);
    padding: var(--v2-space-3);
    // Top-level filter/control bar above the products grid — canonical
    // glass recipe (mixin in _glass-mixins.scss).
    @include v2-glass-panel-flat;
    flex-shrink: 0;
    // Lift the controls bar's stacking context above the products grid so
    // dropdowns rendered inside it (e.g. V2FilterByDeliveryDate) paint over
    // sibling product cards. Each card's backdrop-filter creates its own
    // stacking context, which would otherwise win on document order.
    position: relative;
    z-index: 20;
  }

  &__left-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--v2-space-3);
    flex: 1;
  }

  &__right-controls {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    margin-left: auto;
  }

  &__search {
    position: relative;
    flex: 0 0 280px;

    ::v-deep .v2-input {
      padding-left: var(--v2-space-8);
    }
  }

  &__search-icon {
    position: absolute;
    top: 50%;
    left: var(--v2-space-3);
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;
    color: var(--v2-muted-foreground);
  }

  &__view-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    // Tinted glass — view-toggle icon buttons (grid / compact-grid).
    // Matches V2Button recipe (no `backdrop-filter`) so per-button GPU
    // cost stays flat; reads as inline controls inside the controls bar.
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    background: var(--v2-glass-surface-2);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover {
      background: var(--v2-glass-surface-1);
      border-color: var(--v2-glass-stroke-strong);
      color: var(--v2-foreground);
    }

    &--active {
      background: var(--v2-primary);
      color: var(--v2-primary-foreground);
      border-color: var(--v2-primary);
      backdrop-filter: none;

      &:hover {
        background: var(--v2-primary);
        color: var(--v2-primary-foreground);
      }
    }

    svg {
      width: 22px;
      height: 22px;
    }
  }

  &__toolbar {
    ::v-deep .v2-btn {
      min-height: 38px;
    }
  }

  &__currency {
    flex: 0 0 auto;
  }

  &__wishlist-selector {
    margin-left: auto;
  }

  &__heart-icon {
    color: var(--v2-destructive);
  }

  &__group-heading {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    padding: var(--v2-space-3) 0;
    border-bottom: 1px solid var(--v2-border);
    margin-bottom: var(--v2-space-3);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-4);

    &--compact {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      gap: var(--v2-space-2);
    }
  }

  &__empty {
    width: 100%;
    padding: var(--v2-space-8);
    text-align: center;
  }

  &__multi-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    display: flex;
    justify-content: center;
  }

  &__multi-bar-inner {
    display: flex;
    align-items: center;
    // Glass floating bar — uses --v2-glass-surface-1 (panel recipe, ~4%
    // tint) so the bar reads as visibly translucent glass instead of
    // near-opaque white. Combined with backdrop-filter blur, the page
    // mesh shows through and the bar feels like proper frosted glass.
    background: var(--v2-glass-surface-1);
    backdrop-filter: blur(var(--v2-glass-blur-lg)) saturate(180%);
    color: var(--v2-foreground);
    border: 1px solid var(--v2-glass-stroke);
    border-bottom: none;
    border-top-left-radius: 0.75rem;
    border-top-right-radius: 0.75rem;
    box-shadow: var(--v2-glass-shadow);
    white-space: nowrap;
    overflow-x: auto;
  }

  &__multi-count {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 1.25rem;
    font-weight: 600;
    font-size: 0.875rem;
    min-width: 180px;
  }

  &__multi-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5rem;
    height: 1.5rem;
    padding: 0 0.375rem;
    border-radius: 999px;
    background: var(--v2-primary);
    color: var(--v2-primary-foreground);
    font-size: 0.75rem;
    font-weight: 700;
  }

  &__multi-action {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 1rem 1.25rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: none;
    border: none;
    border-left: 1px solid var(--v2-glass-stroke);
    white-space: nowrap;
    transition: background 0.15s ease;

    &:hover {
      background: var(--v2-glass-surface-2);
    }

    &--danger {
      color: var(--v2-destructive);
    }

    svg {
      flex-shrink: 0;
    }
  }
}

.v2-slide-up-enter-active,
.v2-slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.v2-slide-up-enter,
.v2-slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.v2-text-muted { color: var(--v2-muted-foreground); }
.v2-text-center { text-align: center; }
</style>
